import { FC, useEffect, useMemo, useState } from 'react'
import { Form, replace, Outlet, useMatch, useNavigate, useOutletContext, useParams, useSubmit, useActionData } from 'react-router-dom'
import { useUser } from '@clerk/clerk-react'
import { useStripe } from '@stripe/react-stripe-js'
import './add.css'

// COMPONENTS
import Button from '@components/atoms/button'
import { Column } from '@components/templates/column'
import Card from '@components/atoms/card'
import CurrencyTextField from '@components/molecules/currency-textfield'
import ErrorModal from '@components/molecules/error-modal'
import PaymentMethodCell from '@components/organisims/payment-method-cell'
import WalletOptions from '@components/molecules/wallet-options'
import CreditBalance from '@components/molecules/credit-balance'

// ROUTES
import AddCardRoute from './add-payment-method'

// TYPES
import type { RouteObject } from 'react-router-dom'
import type { Stripe, SetupIntent } from '@stripe/stripe-js'

// UTILITIES
import { max } from '@constants'
import { createResponseObj, FailedResponse, isFailedResponse, StripeAPI, VirtualCardAPI } from '@apis'
import { isNonEmptyString, isNumber, isObject } from '@utils/type-checkers'
import { createLimitedAction } from '@utils/actions'
import { priceFormatter, toPennies } from '@utils/number-helper'
import { useCurrencyInput } from '@hooks/inputs'
import { usePaymentMethods } from '@/hooks/tanstack/usePaymentMethods'
import { useGroupLoaderData } from '../..'


type AddFundsPMContext = [{ paymentMethod?: CreditCard, setupIntent?: SetupIntent }, React.Dispatch<React.SetStateAction<{
    paymentMethod?: CreditCard
    setupIntent?: SetupIntent
}>>]
export const useAddFundsPMOutletContext = () => useOutletContext<AddFundsPMContext>()

let stripe: Stripe | null = null

const formId = "add-more-money"
const Component: FC<{}> = () => {
    const { isSignedIn, user } = useUser()
    const { data: paymentMethods = [] } = usePaymentMethods(user?.id)
    const submit = useSubmit()
    const { pledgeId } = useParams()
    const group = useGroupLoaderData()
    const navigate = useNavigate()
    const isOrganizer = isSignedIn ? group.organizer?.id === user.id : false
    const pledge = useMemo(() => JSON.stringify(group.pledges?.find(({ id }) => id === pledgeId) || ''), [group.pledges, pledgeId])
    const [addMoreAmount, setAddMoreAmount] = useState(0)
    const [addMoreMerchant, setAddMoreMerchant] = useState('')

    const actionData = useActionData()
    const [showError, setError] = useState(false)
    const [error, setErrorData] = useState<FailedResponse>()

    useEffect(() => {
        if (isFailedResponse(actionData)) {
            setErrorData(actionData)
            setError(true)
        }
    }, [actionData])

    const isAdd = useMatch({ path: '/group/:groupId/add/:pledgeId/addPM' })
    const [state, setState] = useState<{ paymentMethod?: CreditCard | ApplePay | GooglePay, setupIntent?: SetupIntent }>({})

    const currentPaymentMethod = useMemo(() => JSON.parse(pledge).paymentMethod, [pledge])
    const method = useMemo(() => JSON.stringify(state.paymentMethod || currentPaymentMethod), [currentPaymentMethod, state.paymentMethod])

    const funds = useMemo(() => (((group.collectedAmount || 0) - (group.spendAmount || 0)) / 100), [addMoreAmount, group.collectedAmount, group.spendAmount])
    const [addAmount, addAmountProps, setAddAmount] = useCurrencyInput({
        initValue: ``,
        max: max,
    })
    const [useCreditBalance, setUseCreditBalance] = useState(false)
    const creditAmount = useMemo(() => (user?.publicMetadata?.creditAmount) || 0, [user?.publicMetadata?.creditAmount])
    const showCreditBalance = false
    const isCreditBalanceApplied = showCreditBalance ? useCreditBalance : false

    // When credit toggle is turned on with no amount entered, auto-fill with credit amount
    function handleCreditToggle(checked: boolean) {
        setUseCreditBalance(checked)
        if (checked && (!addAmount || Number(addAmount) === 0)) {
            // Auto-fill with credit amount in dollars
            setAddAmount(String(creditAmount / 100))
        }
    }

    // Determine if payment method is required
    // Payment method is NOT required only if: credit toggle is ON AND credit covers the full amount
    const requiresPaymentMethod = useMemo(() => {
        const amountInPennies = toPennies(addAmount)
        if (amountInPennies === 0) return false
        if (isCreditBalanceApplied && amountInPennies <= creditAmount) return false
        return true
    }, [isCreditBalanceApplied, addAmount, creditAmount])


    const submitable = useMemo(() => (
        !!addAmount
        && toPennies(addAmount) >= 100
        && toPennies(addAmount) <= max
        && (!requiresPaymentMethod || method !== 'null')
    ), [addAmount, method, requiresPaymentMethod])

    stripe = useStripe()

    const transactions = useMemo(() => {
        const items = group.transactions
        if (!items) return []

        return Object.keys(items).map(key => items[key]).reduce((a, b) => a.concat(b), [])
    }, [group.transactions])

    const remainingFunds = useMemo(() => (group.collectedAmount || 0) - (group.spendAmount || 0), [group.collectedAmount, group.spendAmount]);

    useEffect(() => {
        for (let i = 0; i < transactions.length; i++) {
            if (isOrganizer && transactions[i].declineCode && (transactions[i].amount > remainingFunds)) {
                setAddAmount(`${(transactions[i].amount - remainingFunds) / 100}`)
                setAddMoreAmount((transactions[i].amount - remainingFunds))
                setAddMoreMerchant(transactions[i].merchant.name)
            }
        }
    }, [isOrganizer, remainingFunds, transactions])

    useEffect(() => {
        if (state.paymentMethod?.type === 'apple_pay' || state.paymentMethod?.type === 'google_pay') {
            const form = document.getElementById(formId) as HTMLFormElement
            submit(form)
        }
    }, [state.paymentMethod?.type])

    // Handles resetting the local state after a payment method has been deleted from the users pm array.
    useEffect(() => {
        const paymentMethod = state.paymentMethod
        if (paymentMethod && paymentMethod.type === 'credit_card' && !paymentMethods.find(({ token }) => token === paymentMethod.token))
            setState({})
    }, [paymentMethods, state.paymentMethod])

    function onPaymentConfirmed(intentId: string, paymentMethod?: CreditCard | ApplePay | GooglePay) {
        const formData = new FormData()
        formData.append('user', JSON.stringify(user))
        // Use addAmount directly
        formData.append('amount', addAmount)
        formData.append('pledge', pledge)
        formData.append('paymentIntentId', JSON.stringify(intentId))
        formData.append('useCredit', JSON.stringify(isCreditBalanceApplied))

        if (paymentMethod)
            formData.append('paymentMethod', JSON.stringify(paymentMethod))


        submit(formData, { method: 'POST' })
    }

    function handlePMSelected(card: CreditCard) {
        // Toggle: if clicking the same card, deselect it
        if (state.paymentMethod?.type === 'credit_card' && state.paymentMethod.token === card.token) {
            setState((currentState) => ({
                ...currentState,
                paymentMethod: undefined
            }))
        } else {
            setState((currentState) => ({
                ...currentState,
                paymentMethod: card
            }))
        }
    }

    function handleAdd() {
        navigate('./addPM')
    }

    function handleBack() {
        navigate('../../')
    }

    return (<>
        <Column.Header.Start>
            {!isAdd && <a onClick={handleBack}>
                <img src="/icons/arrow-left.svg" alt="back" style={{ width: '1.4rem', height: '1.4rem' }} draggable="false" />
            </a>}
        </Column.Header.Start>
        {!isAdd && (
            <Form id={formId} method="POST" className="flex flex-col" style={{ gap: 'inherit', textAlign: 'center' }}>
                <input onChange={() => { }} value={pledge} name="pledge" hidden />
                <input onChange={() => { }} value={method} name="paymentMethod" hidden />
                <input onChange={() => { }} value={state.setupIntent?.id || ''} name="setupIntentId" hidden />
                <input readOnly value={String(isCreditBalanceApplied)} name="useCredit" hidden />
                {isOrganizer && !!addMoreAmount &&
                    <Card style={{ backgroundColor: 'var(--splitcard-white)', border: '2px dotted var(--splitcard-red-600)', boxShadow: 'none', marginBottom: '1rem' }}>
                        <p className='text-body text-red-600 text-center'>You were short <strong>{priceFormatter.format(addMoreAmount / 100)}</strong> for an attempted transaction to "{addMoreMerchant}".</p>
                    </Card>
                }
                <h2>Add More Money</h2>
                <p className="text-body-2 text-gray-500">We will add this amount to your group card.</p>
                <CurrencyTextField style={{ flex: 1 }} centered={true} {...addAmountProps} name="amount" />
                {!!addAmount && <Card style={{ backgroundColor: 'var(--splitcard-white)', border: '2px dotted var(--splitcard-gray-200)', boxShadow: 'none' }}>
                    <p className='text-body text-center'>Your available funds will increase to <strong>{priceFormatter.format(Number(addAmount) + funds)}</strong></p>
                </Card>}
            </Form >
        )}
        <h2 style={{ marginTop: '1rem', textAlign: 'center', opacity: requiresPaymentMethod ? 1 : 0.5 }}>Payment Method</h2>
        {(isAdd || !currentPaymentMethod || currentPaymentMethod?.type === 'credit_card') ? null :
            <PaymentMethodCell
                selected={!state.paymentMethod?.lastFour && currentPaymentMethod?.type !== 'credit_card'}
                style={{ borderColor: 'var(--splitcard-blue-500)', opacity: requiresPaymentMethod ? 1 : 0.5, pointerEvents: requiresPaymentMethod ? 'auto' : 'none' }}
                paymentMethod={currentPaymentMethod}
            />
        }
        <PaymentMethodCell
            type={isAdd ? 'empty' : 'add'}
            onClick={requiresPaymentMethod && !isAdd ? handleAdd : undefined}
            style={{ opacity: requiresPaymentMethod ? 1 : 0.5, pointerEvents: requiresPaymentMethod ? 'auto' : 'none' }}
        >
            <Outlet context={[state, setState]} />
        </PaymentMethodCell>
        {!isAdd && showCreditBalance && creditAmount > 0 && (
            <CreditBalance
                creditAmount={creditAmount}
                isApplied={useCreditBalance}
                onToggle={handleCreditToggle}
            />
        )}
        {isAdd ? null : paymentMethods.map((method, index) => (
            <PaymentMethodCell
                key={`${method.token}-${index}`}
                className={method.token === currentPaymentMethod?.token ? '.current-payment-method' : ''}
                style={{
                    borderColor: method.token === currentPaymentMethod?.token ? 'var(--splitcard-blue-500)' : '',
                    opacity: requiresPaymentMethod ? 1 : 0.5,
                    pointerEvents: requiresPaymentMethod ? 'auto' : 'none'
                }}
                paymentMethod={method}
                selected={(state.paymentMethod?.type === 'credit_card' && method.token === state.paymentMethod?.token) || (!state.paymentMethod?.lastFour && currentPaymentMethod?.type === 'credit_card' && method.token === currentPaymentMethod?.token)}
                onClick={requiresPaymentMethod ? () => handlePMSelected(method) : undefined}
                removable />
        ))}
        {(!isAdd) &&
                <WalletOptions
                    groupId={group.id}
                    amount={isCreditBalanceApplied ? Math.max(0, toPennies(addAmount) - creditAmount) : toPennies(addAmount)}
                    onConfirm={onPaymentConfirmed}
                    style={{ opacity: requiresPaymentMethod ? 1 : 0.5, pointerEvents: requiresPaymentMethod ? 'auto' : 'none' }}
                    enterZero={false}
                />
        }
        {isAdd ? null : (<>
            <Column.Footer className="no-keyboard-adjust">
                <Button form={formId} type="submit" disabled={!submitable}>Add Funds</Button>
            </Column.Footer>
        </>)}
        <ErrorModal
            state={[showError, setError]}
            error={error}
            title="Issue Adding Funds"
            fallbackMessage="There was an issue adding funds to the group. If this continues please contact support."
        />
    </>)
}

const action = createLimitedAction({
    limit: 'addAdditionalFunds',
    getRedirectPath: ({ params }) => `/group/${params.groupId}`,
}, async ({ request, params }) => {
    const formData = await request.formData()
    const groupId = params['groupId']
    const pledgeId = params['pledgeId']
    const amount = toPennies(`${formData.get('amount')}` || '')
    const pledge = JSON.parse(`${formData.get('pledge') || ''}`)
    const additionalCharge = true
    const useCredit = formData.get('useCredit') === 'true'

    let paymentMethod: ApplePay | GooglePay | CreditCard | undefined
    if (formData.has('paymentMethod')) {
        paymentMethod = JSON.parse(formData.get('paymentMethod') as string)
    }

    console.log("Add Pledge Data:", { groupId, pledge, pledgeId, amount, paymentMethod })
    if (!isNonEmptyString(groupId) || !isObject<Pledge>(pledge) || !isNonEmptyString(pledgeId) || (!isNumber(amount) || amount < 100)) throw createResponseObj({
        status: 'failed', type: 'local', name: 'missing-resources',
        message: import.meta.env.DEV
            ? `Missing required resources. ${JSON.stringify({ groupId, pledge })}`
            : `Unable to add additional funds. Please contact support if this continues to happen.`
    })

    var paymentIntent = JSON.parse(formData.get("paymentIntentId") as string);
    var setupIntentId = formData.get("setupIntentId") as string;

    // If credit covers the full amount and no payment method, send empty string as transactionId
    if (useCredit && !paymentMethod && !paymentIntent && !isNonEmptyString(setupIntentId)) {
        setupIntentId = ''
    } else if (!paymentIntent && !isNonEmptyString(setupIntentId) && paymentMethod) {
        const { clientSecret } = await StripeAPI.CreateSetupIntentRequest.fetch()
        const { setupIntent, error } = await stripe?.confirmCardSetup(clientSecret, {
            payment_method: paymentMethod.token
        }) || {}

        if (!setupIntent || error) throw error || new Error("Failed to generate Setup intent")
        setupIntentId = setupIntent.id
    }

    await VirtualCardAPI.UpdatePledgeRequest.fetch(groupId, {
        id: pledgeId,
        amount,
        additionalCharge,
        paymentMethod,
        transactionId: paymentIntent || setupIntentId,
        useCredit
    })

    return replace(`/group/${groupId}`)
})

const AddPledgeIdRoute: RouteObject = {
    path: ':pledgeId',
    Component,
    action,
    children: [
        AddCardRoute,
    ],
}

export { AddPledgeIdRoute }
